import React from "react";
import {
  Box, Typography, Stack,
  Button, Divider, Tooltip, Paper,
  Fade,
} from "@mui/material";
import EqualizerIcon from '@mui/icons-material/Equalizer'; 
import StarOutlineIcon from '@mui/icons-material/StarOutline'; 

const QuestionFilterComponent = ({
  groupsById,
  selectedVariable,
  preferenceFilter,
  onPreferenceFilterChange,
  ActionMode,
  side,
  providerFilter,
  providerFilterOriginalVar,
  onProviderFilterChange,
  clientSlug,
  keysWithData,
}) => {
  const currentVar = groupsById?.[selectedVariable];
  const firstLikertGridSubVar = currentVar?.type === "likert_grid"
    ? currentVar?.subVariables?.[0]
    : null;
  const [prefLikertGridSubVarId, prefLikertGridMetricRaw] = String(preferenceFilter || "").split("|");

  const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const detectSentimentPair = (subVar) => {
    const posKey = toArr(subVar?.polarity?.pos)[0];
    const negKey = toArr(subVar?.polarity?.neg)[0];
    const posLabel = String(subVar?.labels?.[posKey] || subVar?.full_labels?.[posKey] || "").toLowerCase();
    const negLabel = String(subVar?.labels?.[negKey] || subVar?.full_labels?.[negKey] || "").toLowerCase();
    const text = `${posLabel} ${negLabel}`;
    if (text.includes("comfort")) {
      return { posToken: "comfort", posLabel: "Comfort", negToken: "discomfort", negLabel: "Discomfort" };
    }
    return { posToken: "trust", posLabel: "Trust", negToken: "distrust", negLabel: "Distrust" };
  };
  const activeLikertGridSubVar = groupsById?.[prefLikertGridSubVarId] || groupsById?.[firstLikertGridSubVar];
  const sentimentPair = detectSentimentPair(activeLikertGridSubVar);
  const metricIsNegative = ["distrust", "discomfort", "neg", "negative"].includes(String(prefLikertGridMetricRaw || "").toLowerCase());
  const metricIsNet = String(prefLikertGridMetricRaw || "").toLowerCase() === "net";
  const isWindfallGrid = String(selectedVariable || "").toLowerCase() === "windfall";

  const isVoteIntention = String(currentVar?.label || "").toLowerCase().includes("vote intention");
  const isDontKnowOption = (key) => {
    const kk = String(key || "").toLowerCase();
    const lbl = String(currentVar?.labels?.[key] || currentVar?.full_labels?.[key] || "").toLowerCase();
    return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
  };
  // Options the selected wave doesn't carry are hidden entirely.
  //
  // A tracking question's option list moves between waves — Andy Burnham and
  // Rupert Lowe joined "best Prime Minister" at wave 5, Keir Starmer left it
  // the same wave — and offering a button that can only ever colour the map
  // with nulls is worse than not offering it. `keysWithData` comes from the
  // sidebar aggregates (utils/keyAvailability.js), so this needs no per-question
  // configuration; an absent or empty list means "not known yet", and then
  // every configured option stays visible.
  // The list is only trusted when it overlaps the question on screen: in target
  // mode the aggregates are computed for the target variable, which need not be
  // the question this picker is showing. No overlap means "nothing to say about
  // these options", and every one of them stays.
  const availableKeys = (() => {
    if (!Array.isArray(keysWithData) || !keysWithData.length) return null;
    const set = new Set(keysWithData);
    const covers = (currentVar?.keys || []).some((k) => set.has(k));
    return covers ? set : null;
  })();
  const hasWaveData = (key) => !availableKeys || availableKeys.has(key);

  // Drill-down options render in CONFIGURED order (as listed in the variable
  // definition) — no value-based sorting. Value sorting made the lists reshuffle
  // whenever the underlying data refetched (drilling in, switching to Find
  // Target Areas), which made options impossible to find twice.
  const visibleVarKeys = (currentVar?.keys || [])
    .filter((k) => !(isVoteIntention && isDontKnowOption(k)))
    .filter(hasWaveData);

  // First sub-variable in configured order = the "top left" option in the grid.
  const topLikertGridSubVar = (currentVar?.subVariables || [])[0] || firstLikertGridSubVar;
  // Target mode ranks likert grids (trust and windfall alike) on the net score.
  const getTargetDefaultMetric = () => "net";

  // --- LOGIC HELPERS ---
  const toggleVal = (curr, next, OG) => (curr === next ? OG : next);
  
  // UPDATED: Logic to fallback to 'net' for likert, 'top' for others
  const getFallback = () => {
    if (currentVar?.["type"] === "likert") return "net";
    if (currentVar?.["type"] === "likert_grid") {
      if (ActionMode === "filter") {
        return firstLikertGridSubVar ? `${firstLikertGridSubVar}|net` : "compare";
      }
      return firstLikertGridSubVar || "compare";
    }
    return ActionMode === "explore" ? "top" : currentVar?.["keys"]?.[0];
  };

  React.useEffect(() => {
    // In target mode, likert-grid should always resolve to a specific sector trust
    // (no compare-all/top-sector mode).
    if (
      currentVar?.type === "likert_grid" &&
      ActionMode === "filter" &&
      topLikertGridSubVar &&
      (!preferenceFilter || preferenceFilter === "compare")
    ) {
      const defaultMetric = getTargetDefaultMetric(topLikertGridSubVar);
      onPreferenceFilterChange?.(defaultMetric ? `${topLikertGridSubVar}|${defaultMetric}` : `${topLikertGridSubVar}`);
    }
  }, [currentVar?.type, ActionMode, topLikertGridSubVar, preferenceFilter, onPreferenceFilterChange, groupsById, isWindfallGrid]);

  // A wave change can pull the option that is currently selected out from under
  // the map — switch back from wave 5 with Andy Burnham selected and there is
  // nothing left to colour. Step back to a view the wave does have rather than
  // leaving a blank map and a selection with no button.
  React.useEffect(() => {
    if (!availableKeys || !preferenceFilter) return;
    const [head, tail] = String(preferenceFilter).split("|");

    // likert_grid: "<sector>|<sentiment key>" — keep the sector, drop to its net.
    if (tail && groupsById?.[head]?.keys?.includes(tail) && !availableKeys.has(tail)) {
      onPreferenceFilterChange?.(`${head}|net`);
      return;
    }
    // Everything else: a bare response key. "top"/"second"/"third"/"net" and
    // sector ids are not response keys, so they pass through untouched.
    if (!tail && currentVar?.keys?.includes(head) && !availableKeys.has(head)) {
      const fallback = currentVar?.type === "likert"
        ? "net"
        : ActionMode === "explore" ? "top" : (visibleVarKeys[0] || "top");
      onPreferenceFilterChange?.(fallback);
    }
  }, [preferenceFilter, availableKeys, currentVar, groupsById, ActionMode, visibleVarKeys, onPreferenceFilterChange]);

  // --- STYLES ---
  
  // Unified Button Style (Used for Top, Net, AND Specific Options now)
  const optionBtnStyle = (isActive) => ({
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    width: "100%",
    minHeight: "3em",
    height: "auto",
    overflow: "visible",
    "& .MuiButton-startIcon": { flexShrink: 0 },
    "& span": { whiteSpace: "normal", overflow: "visible", textOverflow: "unset" },

    fontWeight: isActive ? 600 : 500,
    fontSize: "0.75rem",
    lineHeight: 1.3,
    whiteSpace: "normal",
    wordBreak: "break-word",
    
    boxShadow: "none",
    borderRadius: 2,
    border: "1px solid",
    borderColor: isActive ? "primary.main" : "divider",
    color: isActive ? "primary.main" : "text.secondary",
    bgcolor: isActive ? "rgba(25, 118, 210, 0.08)" : "background.paper",
    
    py: 1,
    px: 1,
    
    '&:hover': {
      bgcolor: isActive ? "rgba(25, 118, 210, 0.15)" : "action.hover",
      borderColor: isActive ? "primary.main" : "text.primary",
    }
  });

  // --- SUB-COMPONENTS ---
  const SectionLabel = ({ title, subtitle }) => (
    <Box sx={{ mt: 2, mb: 1, px: 0.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {title}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1.2, display: 'block', mt: 0.2 }}>
            {subtitle}
        </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", px: 0.5 }}>
      
      {/* GLOBAL HEADER */}

      {/* ========================================================= */}
      {/* LIKERT LOGIC                                              */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "likert" && (
        <>
            {/* ZONE 1: AGGREGATES */}
            <SectionLabel 
                title="Summary Views" 
                subtitle="Aggregated data calculations."
            />
            
            <Box display="grid" gridTemplateColumns={ActionMode === "explore" ? "1fr 1fr" : "1fr"} gap={0.75}>
                {ActionMode === "explore" && (
                <Button
                    fullWidth
                    variant="outlined" 
                    sx={optionBtnStyle(preferenceFilter === "top")}
                    startIcon={<StarOutlineIcon sx={{fontSize: 18}} />}
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, "top", "top"))}
                >
                    Top Option
                </Button>
                )}

                <Button
                    fullWidth
                    variant="outlined"
                    sx={optionBtnStyle(preferenceFilter === "net")}
                    startIcon={<EqualizerIcon sx={{fontSize: 18}} />} 
                    onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, "net", getFallback()))}
                >
                    Net Score
                </Button>
            </Box>

            <Divider sx={{ my: 2, borderStyle: 'dashed' }} />

            {/* ZONE 2: DETAILED LIST (NOW 2 COLUMNS) */}
            <SectionLabel 
                title="Individual Options" 
                subtitle="Select a single option to isolate."
            />

            <Box 
              display="grid" 
              gridTemplateColumns="1fr 1fr" 
              gap={0.75} // Tight spacing
              alignItems="stretch" // Ensures buttons match height in same row
            >
                {visibleVarKeys.map((key) => {
                    const isActive = preferenceFilter === key;
                    return (
                        <Tooltip
                            key={key}
                            title={currentVar?.["full_labels"]?.[key] || ""}
                            placement="left"
                            TransitionComponent={Fade}
                            arrow
                            disableHoverListener={!currentVar?.["full_labels"]?.[key]}
                        >
                            <Button
                                sx={optionBtnStyle(isActive)}
                                onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, key, getFallback()))}
                            >
                                {currentVar?.["labels"]?.[key]}
                            </Button>
                        </Tooltip>
                    );
                })}
            </Box>
        </>
      )}

      {/* ========================================================= */}
      {/* CATEGORICAL LOGIC                                         */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "categorical" && (
        <>
            {ActionMode === "explore" && (
                <>
                <SectionLabel
                    title="Rankings"
                    subtitle="Quickly view the highest performers."
                />
                <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={0.5}>
                    {["top", "second", "third"].map((rank) => (
                        <Button
                            key={rank}
                            fullWidth
                            sx={optionBtnStyle(preferenceFilter === rank)}
                            onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, rank, "top"))}
                        >
                            {rank.charAt(0).toUpperCase() + rank.slice(1)}
                        </Button>
                    ))}
                </Box>
                <Divider sx={{ my: 2, borderStyle: 'dashed' }} />
                </>
            )}

            <SectionLabel 
                title="Specific Categories" 
                subtitle="Isolate a single category."
            />

            <Box 
              display="grid" 
              gridTemplateColumns="1fr 1fr" 
              gap={0.75}
              alignItems="stretch"
            >
                {visibleVarKeys.map((key) => {
                    const isActive = preferenceFilter === key;
                    return (
                        <Tooltip
                            key={key}
                            title={currentVar?.["full_labels"]?.[key] || ""}
                            placement="left"
                            TransitionComponent={Fade}
                            arrow
                            disableHoverListener={!currentVar?.["full_labels"]?.[key]}
                        >
                            <Button
                                sx={optionBtnStyle(isActive)}
                                onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, key, getFallback()))}
                            >
                                {currentVar?.["labels"]?.[key]}
                            </Button>
                        </Tooltip>
                    );
                })}
            </Box>
        </>
      )}

      {/* ========================================================= */}
      {/* LIKERT GRID LOGIC (multi-sector comparison)              */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "likert_grid" && (() => {
        const validSubVarIds = currentVar.subVariables || [];
        const isValidSubVar = validSubVarIds.includes((preferenceFilter || "").split("|")[0]);
        const isCompare = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;
        const [activeSubVarId, activeSubKey] = isCompare ? [] : preferenceFilter.split("|");
        const activeSubVar = activeSubVarId ? groupsById?.[activeSubVarId] : null;
        const isTargetMode = ActionMode === "filter";

        return (
          <>
            {!isTargetMode && (
              <>
                <SectionLabel
                  title="View"
                  subtitle={currentVar.sidebarCompareSubtitle ?? "Compare net trust across all sectors, or drill into one."}
                />

                <Button
                  fullWidth
                  sx={optionBtnStyle(isCompare)}
                  startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onPreferenceFilterChange?.("compare")}
                >
                  {currentVar.sidebarCompareButton ?? "Compare all sectors"}
                </Button>

                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
              </>
            )}

            <SectionLabel
              title={currentVar.sidebarDrillTitle ?? "Drill into a sector"}
              subtitle={isTargetMode
                ? (currentVar.sidebarDrillSubtitleTarget ?? "Select one sector trust score for targeting.")
                : (currentVar.sidebarDrillSubtitle ?? "Select a sector for individual results.")}
            />

            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch">
            {(currentVar.subVariables || []).map((svId) => {
                const isActive = activeSubVarId === svId;
                return (
                  <Button
                    key={svId}
                    sx={optionBtnStyle(isActive)}
                    onClick={() =>
                      onPreferenceFilterChange?.(
                        isTargetMode
                          ? (() => {
                              const nextMetric = isActive
                                ? (activeSubKey || getTargetDefaultMetric(svId))
                                : getTargetDefaultMetric(svId);
                              return nextMetric ? `${svId}|${nextMetric}` : svId;
                            })()
                          : svId
                      )
                    }
                  >
                    {currentVar.subLabels?.[svId] || svId}
                  </Button>
                );
              })}
            </Box>

            {isTargetMode && activeSubVarId && isWindfallGrid && activeSubVar && (
              <>
                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                <SectionLabel
                  title="Target response"
                  subtitle="Net score, or pick one response option for this sector."
                />
                <Button
                  fullWidth
                  sx={optionBtnStyle(metricIsNet)}
                  startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|net`)}
                >
                  Net Score
                </Button>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch" sx={{ mt: 0.75 }}>
                  {(activeSubVar.keys || []).filter(hasWaveData).map((key) => {
                    const isKeyActive = activeSubKey === key;
                    return (
                      <Button
                        key={key}
                        sx={optionBtnStyle(isKeyActive)}
                        onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|${key}`)}
                      >
                        {activeSubVar.labels?.[key] || key}
                      </Button>
                    );
                  })}
                </Box>
              </>
            )}

            {isTargetMode && activeSubVarId && !isWindfallGrid && (
              <>
                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                <SectionLabel
                  title="Target sentiment"
                  subtitle={`Net score, or ${sentimentPair.posLabel.toLowerCase()}/${sentimentPair.negLabel.toLowerCase()} for this sector.`}
                />
                <Button
                  fullWidth
                  sx={optionBtnStyle(metricIsNet)}
                  startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|net`)}
                >
                  Net Score
                </Button>
                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch" sx={{ mt: 0.75 }}>
                  <Button
                    sx={optionBtnStyle(!metricIsNet && !metricIsNegative)}
                    onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|${sentimentPair.posToken}`)}
                  >
                    {sentimentPair.posLabel}
                  </Button>
                  <Button
                    sx={optionBtnStyle(metricIsNegative)}
                    onClick={() => onPreferenceFilterChange?.(`${activeSubVarId}|${sentimentPair.negToken}`)}
                  >
                    {sentimentPair.negLabel}
                  </Button>
                </Box>
              </>
            )}

            {!isTargetMode && activeSubVar && (
              <>
                <Divider sx={{ my: 2, borderStyle: "dashed" }} />
                <SectionLabel
                  title="Result type"
                  subtitle={activeSubVar.label}
                />

                <Button
                  fullWidth
                  sx={optionBtnStyle(!activeSubKey)}
                  startIcon={<EqualizerIcon sx={{ fontSize: 18 }} />}
                  onClick={() => onPreferenceFilterChange?.(activeSubVarId)}
                >
                  Net Score
                </Button>

                <Box display="grid" gridTemplateColumns="1fr 1fr" gap={0.75} alignItems="stretch" sx={{ mt: 0.75 }}>
                  {(activeSubVar.keys || []).filter(hasWaveData).map((key) => {
                    const isKeyActive = activeSubKey === key;
                    return (
                      <Button
                        key={key}
                        sx={optionBtnStyle(isKeyActive)}
                        onClick={() => onPreferenceFilterChange?.(activeSubVarId + "|" + key)}
                      >
                        {activeSubVar.labels?.[key] || key}
                      </Button>
                    );
                  })}
                </Box>
              </>
            )}
          </>
        );
      })()}

      {/* ========================================================= */}
      {/* PRICE ELASTICITY LOGIC                                    */}
      {/* ========================================================= */}
      {currentVar?.["type"] === "price_elast" && (
        <Box sx={{ mt: 1 }}>
            <SectionLabel 
                title="Price Thresholds" 
                subtitle="Standard elasticity definitions."
            />
            {/* Segmented Control Look */}
            <Paper elevation={0} sx={{ bgcolor: 'action.hover', p: 0.5, borderRadius: 2 }}>
                <Stack direction={ActionMode === "explore" ? "row" : "column"} spacing={0.5}>
                {["expect", "expensive", "tooexpensive"].map((pol) => {
                    const val = currentVar?.["polarity"]?.[pol];
                    const isSelected = preferenceFilter === val;
                    
                    return (
                        <Tooltip
                            key={pol}
                            title={currentVar?.["full_labels"]?.[val] || ""}
                            placement="top"
                        >
                        <Button
                            fullWidth
                            sx={{
                                borderRadius: 1.5,
                                color: isSelected ? "text.primary" : "text.secondary",
                                bgcolor: isSelected ? "background.paper" : "transparent",
                                boxShadow: isSelected ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                                fontWeight: isSelected ? 600 : 500,
                                fontSize: "0.75rem",
                                textTransform: "none",
                                py: 0.8,
                                border: isSelected ? "1px solid" : "1px solid transparent",
                                borderColor: isSelected ? "divider" : "transparent",
                                '&:hover': { bgcolor: isSelected ? "background.paper" : "rgba(255,255,255,0.5)" }
                            }}
                            onClick={() => onPreferenceFilterChange?.(toggleVal(preferenceFilter, val, currentVar?.["polarity"]?.expect))}
                        >
                            {currentVar?.["labels"]?.[val]}
                        </Button>
                        </Tooltip>
                    );
                })}
                </Stack>
            </Paper>
        </Box>
      )}

      {/* ========================================================= */}
      {/* PROVIDER FILTER                                           */}
      {/* ========================================================= */}
      {(() => {
        // Resolve the provider config — check the direct variable first,
        // then the active sub-variable (for likert_grid parent like "trust")
        const origVar = providerFilterOriginalVar || selectedVariable;
        const activeSubVar = prefLikertGridSubVarId || null;
        const providerConfig = groupsById?.[origVar]?.providerFilter
          || (activeSubVar && groupsById?.[activeSubVar]?.providerFilter)
          || null;
        if (!providerConfig) return null;
        if (!providerConfig.visibleTo?.includes(clientSlug)) return null;

        const activeKey = providerFilter || null; // null means "All"

        const pillStyle = (isActive) => ({
          textTransform: "none",
          fontWeight: isActive ? 600 : 500,
          fontSize: "0.72rem",
          lineHeight: 1.3,
          whiteSpace: "nowrap",
          boxShadow: "none",
          borderRadius: "16px",
          border: "1px solid",
          borderColor: isActive ? "#0d9488" : "divider",
          color: isActive ? "#0d9488" : "text.secondary",
          bgcolor: isActive ? "rgba(13, 148, 136, 0.08)" : "background.paper",
          py: 0.5,
          px: 1.5,
          minWidth: 0,
          '&:hover': {
            bgcolor: isActive ? "rgba(13, 148, 136, 0.15)" : "action.hover",
            borderColor: isActive ? "#0d9488" : "text.primary",
          },
        });

        return (
          <>
            <Divider sx={{ my: 2, borderStyle: "dashed" }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: "text.secondary",
                fontSize: "0.68rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                mb: 0.75,
                display: "block",
              }}
            >
              Customer of:
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Button
                sx={pillStyle(activeKey === null)}
                onClick={() => onProviderFilterChange?.(null)}
              >
                All
              </Button>
              {providerConfig.providers.map((p) => (
                <Button
                  key={p.key}
                  sx={pillStyle(activeKey === p.key)}
                  onClick={() => onProviderFilterChange?.(p.key)}
                >
                  {p.label}
                </Button>
              ))}
              <Button
                sx={pillStyle(activeKey === "compare")}
                onClick={() => onProviderFilterChange?.("compare")}
              >
                Compare
              </Button>
            </Stack>

          </>
        );
      })()}
    </Box>
  );
};
export default QuestionFilterComponent;
